import { html, raw } from 'onefold';
import { CodeBlock } from '../../components/CodeBlock';
import { Callout } from '../../components/Callout';
import { Playground } from '../../components/Playground';

export function ThemePage(): Node {
  return html`
    <div>
      <h1>Theming</h1>
      <p><code>createTheme</code> manages CSS custom properties reactively. Switch between light/dark (or any custom themes) and the UI updates instantly via CSS variables.</p>

      <h2>Setup</h2>
      ${CodeBlock(`import { createTheme } from 'onefold/theme';

const theme = createTheme({
  light: {
    bg: '#ffffff',
    text: '#1a1a1a',
    primary: '#3b82f6',
    border: '#e5e7eb',
  },
  dark: {
    bg: '#0f172a',
    text: '#e2e8f0',
    primary: '#60a5fa',
    border: '#334155',
  },
}, 'light'); // default theme

// theme.current() → 'light' (reactive signal)
// theme.set('dark') → switch to dark
// theme.toggle()    → cycle between themes
// theme.themes()    → ['light', 'dark']
// theme.tokens()    → { bg: '#ffffff', text: '#1a1a1a', ... }`)}

      <h2>Usage in Components</h2>
      ${CodeBlock(`function ThemeSwitcher(): Node {
  return html\`
    <div>
      <p>Current: \${() => theme.current()}</p>
      <button onclick=\${() => theme.toggle()}>Toggle Theme</button>
      <button onclick=\${() => theme.set('light')}>Light</button>
      <button onclick=\${() => theme.set('dark')}>Dark</button>
    </div>
  \`;
}`)}

      <h2>CSS Custom Properties</h2>
      <p>Use the theme variables in your CSS — they update automatically when the theme changes:</p>
      ${CodeBlock(`/* style.css — use var(--key) where key matches your theme token names */
body {
  background: var(--bg);
  color: var(--text);
}

button {
  background: var(--primary);
  border: 1px solid var(--border);
}

a {
  color: var(--primary);
}`)}

      ${Callout('createTheme sets CSS custom properties on document.documentElement. All elements that reference those variables update instantly.')}

      <h2>Persistence</h2>
      <p>The selected theme is automatically persisted to localStorage. On page reload, the user's preference is restored.</p>

      <h2>System Preference Detection</h2>
      ${CodeBlock(`// Respect prefers-color-scheme on first visit
const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
const theme = createTheme(themes, prefersDark ? 'dark' : 'light');`)}

      <h2>API</h2>
      ${raw(`<table>
        <tr><th>Property</th><th>Type</th><th>Description</th></tr>
        <tr><td><code>theme.current()</code></td><td>Signal&lt;string&gt;</td><td>Current theme name (reactive).</td></tr>
        <tr><td><code>theme.set(name)</code></td><td>(string) =&gt; void</td><td>Switch to a named theme.</td></tr>
        <tr><td><code>theme.toggle()</code></td><td>() =&gt; void</td><td>Cycle through available themes.</td></tr>
        <tr><td><code>theme.themes()</code></td><td>() =&gt; string[]</td><td>List available theme names.</td></tr>
        <tr><td><code>theme.tokens()</code></td><td>() =&gt; ThemeTokens</td><td>Get current theme's CSS variable values.</td></tr>
      </table>`)}

      ${Playground(`import { createSignal, html, mount } from 'onefold';
import { createTheme } from 'onefold/theme';

function App() {
  // createTheme sets CSS custom properties on :root
  const theme = createTheme({
    light: { bg: '#ffffff', text: '#1a1a2e', primary: '#3b82f6', border: '#e5e7eb', badge: '#f1f5f9', badgeText: '#64748b' },
    dark:  { bg: '#1e293b', text: '#e2e8f0', primary: '#60a5fa', border: '#334155', badge: '#334155', badgeText: '#94a3b8' },
  }, 'light');

  return html\`
    <div>
      <h3>Theme: \${() => theme.current()}</h3>
      <div style="display:flex;gap:8px;margin:12px 0">
        <button onclick=\${() => theme.toggle()}>Toggle</button>
        <button onclick=\${() => theme.set('light')}>Light</button>
        <button onclick=\${() => theme.set('dark')}>Dark</button>
      </div>
      <div style="margin-top:16px;padding:20px;border-radius:8px;background:var(--bg);color:var(--text);border:1px solid var(--border);transition:all 0.3s">
        <h4 style="margin-bottom:8px;color:var(--primary)">Card Component</h4>
        <p style="font-size:14px">This card uses CSS variables set by createTheme. No inline style switching needed.</p>
        <span style="display:inline-block;padding:4px 10px;border-radius:4px;font-size:12px;margin-top:8px;background:var(--badge);color:var(--badgeText)">\${() => theme.current()} mode</span>
      </div>
      <p style="margin-top:12px;font-size:12px;color:#888">Available themes: \${() => theme.themes().join(', ')}</p>
    </div>
  \`;
}

mount(App(), document.getElementById('app'));`, 'createTheme API — Reactive CSS Custom Properties')}

      <h2>Next Steps</h2>
      <ul>
        <li><a href="/core/css">Scoped CSS</a> — component-scoped styles that avoid global collisions</li>
        <li><a href="/i18n">i18n</a> — internationalization with reactive translations</li>
      </ul>
    </div>
  `;
}
